import React, { useState } from 'react';
import { Sparkles, Cpu, Check } from 'lucide-react';
import ToolDescription from './ToolDescription';

const adjectives = [
  // Tech-themed
  'Agile', 'Swift', 'Dynamic', 'Quantum', 'Stellar', 'Digital', 'Cyber', 'Cloud',
  'Neural', 'Binary', 'Atomic', 'Vector', 'Pixel', 'Crypto', 'Sonic', 'Robotic',
  'Virtual', 'Nanotech', 'Encrypted', 'Autonomous', 'Biometric', 'Holographic',
  'Synaptic', 'Algorithmic', 'Reactive', 'Modular', 'Polymorphic', 'Exascale',
  
  // Power-themed
  'Epic', 'Elite', 'Supreme', 'Ultra', 'Mega', 'Super', 'Hyper', 'Prime', 'Dominant',
  'Invincible', 'Unstoppable', 'Apex', 'Vanguard', 'Valiant', 'Ironclad', 'Sovereign',
  'Triumphant', 'Colossal', 'Formidable', 'Relentless',
  
  // Nature-themed
  'Thunder', 'Lightning', 'Solar', 'Cosmic', 'Lunar', 'Storm', 'Aurora', 'Galactic',
  'Volcanic', 'Tidal', 'Magnetic', 'Geothermal', 'Hurricane', 'Cyclone', 'Nebular',
  'Terrestrial', 'Astral', 'Meteoric', 'Oblivion', 'Abyssal',
  
  // Quality-themed
  'Precision', 'Infinite', 'Ultimate', 'Maximum', 'Optimal', 'Rapid', 'Blazing',
  'Efficient', 'Turbo', 'Accelerated', 'Synchronized', 'Calculated', 'Luminous',
  'Resonant', 'Exponential', 'Recursive', 'Parallel', 'Asynchronous', 'Heuristic'
];

const nouns = [
  // Tech teams
  'Ninjas', 'Dragons', 'Phoenix', 'Warriors', 'Titans', 'Wizards', 'Pirates',
  'Hackers', 'Pioneers', 'Rangers', 'Guardians', 'Knights', 'Masters', 'Coders',
  'Engineers', 'Architects', 'Operators', 'Analysts', 'Strategists', 'Samurai',
  'Gladiators', 'Sentinals', 'Crusaders', 'Nomads', 'Voyagers', 'Cipher',
  
  // Tech concepts
  'Algorithms', 'Bytes', 'Pixels', 'Vectors', 'Matrices', 'Pods', 'Nodes',
  'Blockchain', 'Firewall', 'Framework', 'Kernel', 'Compiler', 'Syntax',
  'Firewall', 'Middleware', 'Firmware', 'Blockchain', 'Cache', 'Daemon',
  
  // Mythical creatures
  'Griffins', 'Hydras', 'Krakens', 'Chimeras', 'Oracles', 'Legends', 'Sphinxes',
  'Valkyries', 'Basilisks', 'Leviathans', 'Phoenixes', 'Yetis', 'Minotaurs',
  'Pegasi', 'Golems', 'Banshees', 'Doppelgangers', 'Djinn', 'Ifrits',
  
  // Animals
  'Panthers', 'Wolves', 'Falcons', 'Sharks', 'Vipers', 'Eagles', 'Lions',
  'Hawks', 'Jaguars', 'Scorpions', 'Rhinos', 'Condors', 'Pumas', 'Cobras',
  'Tarantulas', 'Komodos', 'Mantises', 'Wolverines', 'Barracudas',
  
  // Abstract concepts
  'Dynamics', 'Innovation', 'Frontiers', 'Horizons', 'Paradox', 'Infinity',
  'Nexus', 'Momentum', 'Zenith', 'Eclipse', 'Synergy', 'Catalyst', 'Pinnacle',
  'Vortex', 'Genesis', 'Oblivion', 'Apex', 'Eternity', 'Dominion'
];

const TeamNameGenerator: React.FC = () => {
  const [teamName, setTeamName] = useState('');
  const [generatedNames, setGeneratedNames] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [animationComplete, setAnimationComplete] = useState(false);

  const generateTeamName = () => {
    setIsGenerating(true);
    setAnimationComplete(false);
    setDisplayText('');
    
    // Simular procesamiento de IA
    setTimeout(() => {
      const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
      const noun = nouns[Math.floor(Math.random() * nouns.length)];
      const newName = `${adj} ${noun}`;
      setTeamName(newName);
      animateText(newName);
      setGeneratedNames(prev => [newName, ...prev].slice(0, 10)); // Keep last 10 names
    }, 1500);
  };

  const animateText = (text: string) => {
    let i = 0;
    const interval = setInterval(() => {
      if (i <= text.length) {
        setDisplayText(text.substring(0, i));
        i++;
      } else {
        clearInterval(interval);
        setIsGenerating(false);
        setAnimationComplete(true);
      }
    }, 50);
  };

  const setAsTeamName = (name: string) => {
    // Disparar un evento personalizado para comunicarse con el componente TeamMembersList
    const event = new CustomEvent('team-name-selected', { 
      detail: { teamName: name } 
    });
    window.dispatchEvent(event);
  };

  return (
    <div className="space-y-6 p-4">
      <ToolDescription 
        title="Team Name Generator"
        description="Create unique, memorable team names that foster team identity and belonging. A strong team name helps build psychological safety and team cohesion, essential elements for high-performing development teams."
        benefits={[
          "Build team identity and pride",
          "Create memorable team brands for presentations",
          "Foster team bonding and psychological safety",
          "Generate creative names for hackathons and projects",
          "Establish team culture from day one"
        ]}
        howToUse="Simply click the generate button to create a unique team name. Each name combines tech-inspired adjectives with powerful nouns to create names that resonate with development teams. Keep generating until you find the perfect fit!"
      />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Sparkles size={24} className="text-yellow-500" />
          Team Name Generator
        </h2>

        <div className="text-center space-y-4 mt-4">
          <button
            onClick={generateTeamName}
            disabled={isGenerating}
            className="relative px-6 py-3 rounded-lg font-semibold shadow-md transform transition-transform hover:scale-105 hover:shadow-lg overflow-hidden group disabled:opacity-70"
          >
            {/* Capa de degradado */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-500 to-blue-600 
                         dark:from-blue-500 dark:via-purple-400 dark:to-blue-500
                         animate-shimmer bg-size-200"></div>
            
            {/* Contenido del botón */}
            <div className="relative flex items-center justify-center gap-2 text-white">
              {isGenerating ? (
                <>
                  <Cpu className="animate-pulse" size={20} />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="animate-[pulse_3s_ease-in-out_infinite]" size={20} />
                  <span>Generate Team Name</span>
                </>
              )}
            </div>
          </button>

          {(isGenerating || displayText) && (
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg shadow-inner transition-all duration-300 ease-in-out mt-6">
              {isGenerating && displayText === '' && (
                <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                  <div className="animate-pulse flex space-x-2 mb-3">
                    <div className="h-2 w-10 bg-gray-300 dark:bg-gray-700 rounded"></div>
                    <div className="h-2 w-16 bg-gray-300 dark:bg-gray-700 rounded"></div>
                    <div className="h-2 w-8 bg-gray-300 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              )}
              
              {displayText && (
                <div className={`transition-opacity duration-500 ${animationComplete ? 'opacity-100' : 'opacity-90'}`}>
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                    {displayText}
                    {!animationComplete && (
                      <span className="inline-block w-1 h-6 ml-1 bg-blue-500 dark:bg-blue-400 animate-blink"></span>
                    )}
                  </h3>
                </div>
              )}
            </div>
          )}
        </div>

        {generatedNames.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              Recent Names
            </h3>
            <div className="space-y-2">
              {generatedNames.map((name, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-800 dark:text-gray-200 flex items-center justify-between group relative"
                >
                  <span>{name}</span>
                  <button
                    onClick={() => setAsTeamName(name)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md text-sm"
                  >
                    <Check size={14} />
                    <span>Set this name to my team</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamNameGenerator; 